/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { ProductsTable } from 'components/Tables/ProductsTable/ProductsTable';
import { AppLoader } from 'components/Core/AppLoader';
import { Button } from 'components/Core/Button';
import SelectComponent from 'components/Inputs/SelectComponent';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect';
import { CreatedBySelect } from 'components/Inputs/CreatedBySelect';
import EntityOption from 'components/Core/EntityOption';
import IsDeletedSelect from 'components/Inputs/IsDeletedSelect';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { IBrandRef } from 'types/common';
import { availableRoles, DEFAULT_SELECT_STYLES } from './constants';
import { productsTranslations } from './translations';
import { IProductsFilters, ProductsTransFields } from './types';
import styles from './products.module.scss';

const Products: FC = observer(() => {
  const {
    productsStore: { productsCategories, getProductsCategories, productsQuery, getProductsList },
    brandsStore: { getAutoCompleteBrands },
    tableStore: { startPageIndex },
    mainStore: { user },
  } = useStore();

  const { t } = useMyTranslations<ProductsTransFields>([productsTranslations.name]);

  const navigate = useNavigate();

  const { isLoading: isLoadingCategories, mutate: getCategories } = useMutation(getProductsCategories, {
    mutationKey: 'getProductsCategories',
    retry: false,
  });

  const { isLoading, mutate } = useMutation(getProductsList, {
    mutationKey: 'getProductsList',
    retry: false,
  });

  useEffect(() => {
    if (!startPageIndex) {
      mutate(productsQuery);
      getCategories();
    }
  }, [user?.selectedCountryCode]);

  const useFormMethods = useForm<IProductsFilters>({
    defaultValues: {
      ...productsQuery,
    },
  });
  const { handleSubmit } = useFormMethods;

  const renderBrandOption = useCallback((data: IBrandRef) => {
    return <EntityOption image={data.logoUrl} entityType='brands' name={data.name} />;
  }, []);

  const onSubmit = useCallback((data: IProductsFilters) => {
    mutate(data);
  }, []);

  const showBtn = useMemo(() => user && availableRoles.includes(user.role), [user?.role]);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <h2 className={styles.headerTitle}>{t('productsList')}</h2>
      </header>
      <FormProvider {...(useFormMethods as any)}>
        <div className={styles.filters}>
          <AutocompleteSelect
            name='brand'
            placeholder={t(`brand`)}
            data={[]}
            styleProps={DEFAULT_SELECT_STYLES}
            onSearch={getAutoCompleteBrands}
            optionLabel='name'
            optionValue='id'
            customOption={renderBrandOption}
            customSingleValue={renderBrandOption}
          />
          <SelectComponent
            name='categories'
            placeholder={t('categories')}
            options={productsCategories || []}
            optionLabel='categoryName'
            optionValue='id'
            styleProps={DEFAULT_SELECT_STYLES}
            isDisabled={isLoadingCategories}
            isMulti
          />
          <IsDeletedSelect />
          <CreatedBySelect />
          <ControlledInput
            name='search'
            type='text'
            placeholder={t('search')}
            isRemoveMargin
            classNameProp={styles.searchInput}
          />
          <Button
            title={t('search')}
            onClick={handleSubmit(onSubmit)}
            classNameProp={`${styles.btn} ${styles.searchBtn}`}
            isDisabled={isLoading}
          />
          {showBtn && <Button title={t('addBtn')} onClick={() => navigate('add')} classNameProp={styles.btn} />}
        </div>
      </FormProvider>
      {isLoading ? <AppLoader /> : <ProductsTable />}
    </main>
  );
});

export default Products;
